// Shared Constants: Business Rules
// This file is the single source of truth for business rule constants.
// It is copied to frontend/src/shared/ and backend/src/shared/ by sync-shared.js
//
// SAAS-COUPLING: bu dosyadaki oranlar, eşikler ve yerel varsayımlar TrioCare'e özel — hardcoded —
// extraction'da tenant ayar tablosuna taşınır (KDV oranı ülkeye, komisyon oranları
// pazaryeri sözleşmesine, indirim/teslimat eşikleri işletmeye göre değişir);
// kod sabit yerine tenant ayarını okur, sabitler varsayılan değer olarak kalır.

// Teslimat
export const VARSAYILAN_TESLIMAT_GUNU = 10; // iş günü

// Teklif
export const TEKLIF_GECERLILIK_GUN = 28; // takvim günü — yeni teklifin varsayılan geçerlilik süresi

// Teklifin dili: siparisler.teklif_dili, CHECK ile aynı küme. Yalnız teklif ve proformada
// geçerlidir; sipariş formu, e-posta ve kargo belgeleri Türkçe kalır.
export const TEKLIF_DILI = { TR: 'TR', EN: 'EN' } as const;
export const ALL_TEKLIF_DILI = Object.values(TEKLIF_DILI);
export type TeklifDili = typeof TEKLIF_DILI[keyof typeof TEKLIF_DILI];

// Siparişin para birimi: siparisler.para_birimi, CHECK ile aynı küme. Satırın para kolonları
// bu birimdedir, `pivot_fiyati` hariç (ürün kartından yazılır, hep TL). TRY dışı birim yalnız
// TEKLIF satırında bulunabilir, veritabanı da zorlar; ciro ve raporlar teklifi dışarıda bırakır.
// SAAS-COUPLING: küme ve TRY varsayılanı TrioCare'in çalıştığı birimler — hardcoded —
// extraction'da firma ayarından okunur (temel birim firmaya göre değişir).
export const PARA_BIRIMI = { TRY: 'TRY', USD: 'USD', EUR: 'EUR' } as const;
export const ALL_PARA_BIRIMI = Object.values(PARA_BIRIMI);
export type ParaBirimi = typeof PARA_BIRIMI[keyof typeof PARA_BIRIMI];
// Anahtar açılınca seçili gelen birim; kullanıcı değiştirebilir.
export const IHRACAT_TEKLIFI_PARA_BIRIMI = PARA_BIRIMI.USD;

// Proformanın varsayılan koşul metni: Koşullar kutusu boşsa basılır, kutu da bunu gösterir.
// Tek kaynak: PDF, önizleme ve form aynı metni okur. Metin veridir, fonksiyon değil:
// `sync-shared.js` tipli fonksiyonu CommonJS'e çeviremez.
// SAAS-COUPLING: metinler TrioCare'in ticari şartları (KDV, imalat süresi, kargo, garanti;
// ihracatta teslim şekli, ödeme, menşe) — hardcoded — extraction'da kiracı ayarından gelir.
export const TEKLIF_KOSUL_METNI = {
    IC: `• Fiyatlarımıza KDV dâhildir.
• Sipariş üzerine imal edilen bu ürünlerin imal süresi sipariş edilen ürün adetine göre değişmektedir. Ortalama 15-20 iş günüdür.
• İmalat aşağıda verilen hesap bilgilerine havale veya EFT gerçekleştirildikten sonra başlayacaktır.
• Fiyatlarımız kargo ve/veya nakliye dahildir.
• Ürünlerimizin garanti süresi fatura tarihinden itibaren 2 yıldır.
• İşbu fiyat teklifi ${TEKLIF_GECERLILIK_GUN / 7} hafta boyunca geçerlidir.`,
    IHRACAT: {
        TR: `TESLİM ŞEKLİ: EXW İZMİR FABRİKA
ÖDEME ŞEKLİ: %100 PEŞİN ÖDEME
MENŞEİ: TÜRKİYE
İMALAT SÜRESİ: PEŞİN ÖDEMEDEN SONRA 3-4 HAFTA`,
        EN: `DELIVERY TERMS: EXW İZMİR FACTORY
PAYMENT TERMS: 100% ADVANCE PAYMENT
COUNTRY OF ORIGIN: TURKEY
MANUFACTURING TIME: BETWEEN 3-4 WEEKS AFTER ADVANCE PAYMENT`
    }
};

// Termin durumu eşikleri
export const TERMIN_YAKIN_GUN = 5; // ≤ bu gün sayısı → sarı/yakın

// İndirim
export const NAKIT_HAVALE_INDIRIM_ORANI = 10; // %10

// Yerel ülke
// SAAS-COUPLING: yerel ülke = Türkiye — hardcoded — extraction'da tenant ayarına taşınır.
// Değer üç yerde aynı olmak zorunda: burada, shared/data/countries.json'daki TR adında ve
// siparisler.ulke / fatura_ulke kolonlarının DB varsayılanında. Biri değişirse diğerleri kayar.
export const YEREL_ULKE = 'Türkiye';

// Vergi
export const KDV_ORANI = 10; // % — iç pazar (ihracat hariç)

// Trendyol komisyon
export const TRENDYOL_VARSAYILAN_KOMISYON = 21.00; // % (webhook'ta yoksa)
export const TRENDYOL_PLATFORM_HIZMET_BEDELI = 10.19; // TL

// Hepsiburada komisyon
// Gerçek komisyon sipariş verisinden çekilmiyor; raporlarda tahmini oran kullanılır.
export const HEPSIBURADA_VARSAYILAN_KOMISYON = 20.00; // % (tahmini)

// Finansal tolerans
export const PARA_TOLERANS_INDIRIM = 0.5; // TL — indirim sapma toleransı
export const PARA_TOLERANS_HASSAS = 0.01; // TL — fiyat karşılaştırma toleransı

// ─────────────────────────────────────────────────────────────────────────────
// Alan sınırları (rota 1.20)
//
// Bu bloktaki değerler ya bir DB kolonunun genişliğinden ya da bir iş kuralından
// türer; hiçbiri tenant ayarı DEĞİL, o yüzden dosyanın başındaki SAAS-COUPLING
// notu bunları kapsamaz.
//
// Neden tek yer: sınır iki tarafta ayrı yazılınca kayma SESSİZ olur. İki yönü de
// canlıda görüldü — backend sıkı kalırsa kullanıcı "Sunucu hatası" görüp neden
// kaydedemediğini anlamıyor (grup adı, Postgres 22001), frontend sıkı kalırsa
// kullanıcı sınırın gevşetildiğini hiç öğrenmiyor (ürün notu: `d7e7fa5` sınırı
// 300'den 400'e çıkardı, `ProductNoteModal` 300'de kaldı ve beş aydır kimse
// 300'ün üstünde not yazamadı).
//
// KURAL: bir sınırı değiştirirken kolonu da genişlet/daralt. Kolon karşılığı olan
// sabitin yanında hangi kolondan geldiği yazıyor.
// ─────────────────────────────────────────────────────────────────────────────

// İçerik limitleri
export const URUN_NOT_MAX_KARAKTER = 400; // prf_urun_ozel_not max (JSONB, kolon sınırı yok)

// Ürün kartındaki serbest not: urunler.urun_notu TEXT, kolon sınırı yok; iş kuralı.
// Proforma satırının notu (`URUN_NOT_MAX_KARAKTER`) ayrı bir alandır.
export const URUN_KARTI_NOTU_MAX_KARAKTER = 5000;

// Ürünün İngilizce adı: urunler.urun_ad_ingilizce VARCHAR(255).
export const URUN_INGILIZCE_AD_MAX = 255;

// Malzeme fiyat değişikliğinin notu: malzeme_fiyat_gecmisi.fiyat_notu TEXT, kolon
// sınırı yok; iş kuralı (kısa açıklama: zammın tarihi, düzeltme olduğu).
export const MALZEME_FIYAT_NOTU_MAX = 200;

// Toplantılar: başlık, not metni ve CRM dışı katılımcının adı. Kolonlar TEXT,
// kolon sınırı yok; iş kuralı. Şema ve ekran aynı sabitten okur.
export const TOPLANTI_BASLIK_MAX = 200;
export const TOPLANTI_NOTU_MAX = 2000;
export const KATILIMCI_AD_MAX = 100;

// Müşteri grubu
export const MUSTERI_GRUBU_AD_MAX = 100; // musteri_gruplari.ad VARCHAR(100)

// En fazla kaç teslimat adresi tutulur. Kolon değil, iş kuralı: sınır tek satırlık
// bir sayı olduğu için iki tarafta da elle yazılıydı ve ikisi de "3" diyordu.
export const MAX_TESLIMAT_ADRESI = 3;

// TC kimlik numarası: siparisler.tc_kimlik_no VARCHAR(11). Backend'de desen
// (`\d{11}`), frontend'de `maxLength` olarak kullanılır — ikisi de bu sayıdan türer.
export const TC_KIMLIK_UZUNLUK = 11;

// Şifre. Üst sınır bcrypt'in 72 baytı sessizce kırpmasına karşı DoS tavanı;
// alt sınır dört backend şemasında ve üç frontend ekranında ayrı yazılıydı.
export const SIFRE_MIN_UZUNLUK = 8;
export const SIFRE_MAX_UZUNLUK = 128;

// Davet bağlantısının geçerlilik süresi (saat). Bir hafta sonunu kapsar; yanlış
// adrese giden bağlantının açık kaldığı süreyi kısa tutar, admin yeniden gönderebilir.
// Sunucu süreyi ve e-posta metnini, davet formu açıklama satırını buradan kurar.
export const DAVET_GECERLILIK_SAAT = 72;

// Hesap güvenliği. Her firmada aynı (bir kez verilmiş iş kararı); tenant ayarı değil.
// Sunucu, e-posta metni ve ekranlar değeri buradan okur; metne süre elle yazılmaz.
// Art arda bu kadar yanlış şifrede hesap kilitlenir (kilit sorgusu ve giriş servisi).
export const HESAP_KILIT_DENEME_SAYISI = 5;
// Kilidin süresi (dakika): giriş servisi ve kullanıcı listesinin kilit rozeti.
export const HESAP_KILIT_SURESI_DAKIKA = 15;
// Oturum süresi (saat): oturum çerezi ve JWT aynı değerden.
export const OTURUM_SURESI_SAAT = 24;
// Şifre sıfırlama bağlantısının geçerlilik süresi (saat).
export const SIFRE_SIFIRLAMA_GECERLILIK_SAAT = 1;
// Profil fotoğrafının en büyük boyutu (MB); ürün görselinin sınırı ayrıdır.
export const PROFIL_FOTOGRAFI_MAKS_MB = 5;

// Takip (follow-up) özeti — boş/anlamsız kayıt engeli.
export const TAKIP_OZET_MIN_KARAKTER = 3;

// Müşteri arama kutusu: bu uzunluğun altında sorgu atılmaz (backend de boş döner).
export const MUSTERI_ARAMA_MIN_KARAKTER = 2;

// Kargo
// SAAS-COUPLING: kargo firması kod kümesi, varsayılanı ve kargosuz teslimat
// yöntemleri TrioCare'in anlaştığı taşıyıcılardan türüyor — hardcoded —
// extraction'da tenant ayarına taşınır; taşıyıcı eklemek kod değil veri işi olmalı.
//
// Kolonun değer kümesi `KARGO_FIRMALARI`'dır (aşağıda). Arayüzün açılır listesi ve
// pazaryeri eşleme tabloları o kümeye uymak zorunda; `kargoFirmalariKapsar.test.js`
// bunu doğrular.
//
// Kargo firması bilinmiyorsa yazılan değer. Shopify normalizasyonu, snapshot'sız
// webhook güncellemesi ve kargo fişi PDF'i üçü de buradan okur.
export const VARSAYILAN_KARGO_FIRMASI = 'NAKKARGO';

// Kargo firması alanına yazılan ama gerçek taşıyıcı olmayan teslimat yöntemleri.
// "Kargo faturası tamamlanmadı" filtresi ve "kargo bilgisi eksik" raporu bunları
// dışarıda bırakır — bu siparişlerde beklenen bir kargo faturası yoktur.
export const KARGOSUZ_TESLIMAT_YONTEMLERI = ['FABRIKA_TESLIM', 'KENDI_ARACIMIZLA', 'ALICI_ODEMELI'];

// Yukarıdaki listenin SQL IN(...) gövdesi. Kullanım:
//   `AND kargo_firmasi NOT IN (${SQL_KARGOSUZ_TESLIMAT})`
export const SQL_KARGOSUZ_TESLIMAT = KARGOSUZ_TESLIMAT_YONTEMLERI.map((v) => `'${v}'`).join(', ');

// `kargo_firmasi` kolonuna yazılabilecek TÜM kodlar — tek kaynak (rota 1.21).
// Arayüzün açılır listesi ve pazaryeri eşleme tabloları bu kümeye uymak zorunda:
// kolonda listede olmayan bir değer bulunursa Select seçili değeri gösteremez ve
// kullanıcı başka bir alanı kaydederken kargo firması silinebilir.
//
// İlk dokuz kod elle seçilebilen küme; kalan yedisini yalnız pazaryeri eşlemeleri
// (config/trendyol.js, config/hepsiburada.js) üretir. Ayrımı korumak için sıra
// böyle: dropdown'da önce günlük kullanılanlar görünür.
//
// `kargoFirmalariKapsar.test.js` her eşleme hedefinin bu listede olduğunu doğrular;
// yeni bir taşıyıcı eşlemesi eklenip buraya yazılmazsa test kırmızıya döner.
export const KARGO_FIRMALARI = [
    'NAKKARGO',
    'EGE_ULASIM',
    'HOROZ',
    'ARAS',
    'GPSLINE',
    'KENDI_ARACIMIZLA',
    'FABRIKA_TESLIM',
    'ALICI_ODEMELI',
    'Diger',
    // Yalnız pazaryeri eşlemelerinden gelenler
    'YURTICI',
    'MNG',
    'SURAT',
    'PTT',
    'SENDEO',
    'HEPSIJET',
    'TRENDYOL_EXPRESS'
];

// İade / telafi nedeni
// `siparisler.iade_nedeni` DB'de ENUM değil, TEXT — ama pratikte kapalı bir küme:
// değeri ya arayüzün açılır listesi ya da Shopify iade webhook'u yazar. İki taraf
// ayrı listeler tutarsa webhook'un yazdığı bir neden arayüzde karşılıksız kalır;
// `OrderIadeInfoCard` o değeri açılır listede gösteremez ve kullanıcı başka bir
// alanı kaydederken neden silinir — kargo firmasında yaşanan hatanın aynısı
// (rota 1.21). Küme bu yüzden tek yerde; etiketler UI tarafında (rota 2.2).
//
// `iadeNedenleriKapsar.test.js` webhook'un yazdığı değerin bu listede olduğunu
// doğrular.
export const IADE_NEDENLERI = [
    'HASARLI',
    'YANLIS_URUN',
    'MUSTERI_VAZGECTI',
    'EKSIK_URUN',
    'DIGER'
];

// Shopify iade webhook'u sipariş nedenini bilmediği için bunu yazar.
export const WEBHOOK_IADE_NEDENI = 'MUSTERI_VAZGECTI';

// ─────────────────────────────────────────────────────────────────────────────
// Tarih sınırları (rota 3.35)
//
// ALT SINIR — canlıda kullanıcı girişli on üç tarih kolonunda en eski gerçek
// değer 2025-08-07; 2000 öncesi meşru bir kayıt yok. Sınır olmadığı için bir
// sipariş yıl 1 tarihi aldı ve o kayıt aylarca kaydedilemez hâle geldi.
//
// ÜST SINIR — KAYAN, sabit bir yıl DEĞİL. Asıl kaza uzak gelecekte oluyor:
// bozuk değer tarih kutusuna dört haneden küçük yıl olarak düşünce tarayıcı
// onu Amerikan sırasıyla okuyup makul görünen bir tarih uyduruyor (ölçüldü:
// `1-12-31` → ekranda 12.01.2031) ve tek ok tuşuyla kaydedilebiliyor. Sabit
// bir tavan (ör. 2100) bunu yakalamaz. Canlıdaki en ileri meşru değer 20 ay
// sonrası; üç yıl rahat pay bırakır.
//
// ⚠️ ÜST SINIR İSTEK ANINDA HESAPLANIR. Joi'nin `.max(tarih)` kuralı değeri
// şema kurulurken donduruyor (ölçüldü: `describe().rules` içine o günün tarihi
// yazılıyor) ve şemalar sunucu açılışında bir kez kuruluyor — sunucu günlerce
// çalışırsa sınır sessizce geride kalırdı. Bu yüzden doğrulama Joi'nin hazır
// kuralıyla değil, her çağrıda tavanı yeniden okuyan elle yazılmış bir kuralla
// yapılır.
//
// ⚠️ HESAPLAMA BURADA DEĞİL, İKİ TARAFIN `timeUtils`'İNDE. Burada yazılsaydı
// günü sunucunun yerel saatiyle bulurdu ve sunucu UTC'de koşuyor: İstanbul'da
// gece 00:00–03:00 arasında tavan bir gün geri kayıyordu (ölçüldü). Gün sınırı
// üreten her hesap `timeUtils` üzerinden geçmek zorunda — bu dosya `timezone`
// sabitini import edemiyor, çünkü `sync-shared.js`'in CommonJS çevirisi import
// satırı tanımıyor. O yüzden burada yalnız SAYI durur, takvim işi orada yapılır.
// ─────────────────────────────────────────────────────────────────────────────

export const TARIH_ALT_SINIR = '2000-01-01';
export const TARIH_UST_SINIR_YIL = 3;
